import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Target, FileCheck } from 'lucide-react';
import { KPIService } from '../../services/kpiService';

/**
 * Model Builder KPI Dashboard
 * - First-pass approval rate
 * - Projects completed
 * - Corrections count
 */
const ModelBuilderDashboard = ({ employeeId, startDate, endDate, periodLabel }) => {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!employeeId) {
        setError('No employee ID found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await KPIService.getModelBuilderKPIs(employeeId, startDate, endDate);
        setKpis(data);
      } catch (err) {
        console.error('Error fetching model builder KPIs:', err);
        setError('Failed to load KPI data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [employeeId, startDate, endDate]);

  if (loading) {
    return <div className="kpi-dashboard-loading">Loading...</div>;
  }

  if (error) {
    return <div className="kpi-dashboard-error">{error}</div>;
  }

  const getApprovalRateColor = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'danger';
  };

  return (
    <div className="kpi-dashboard">
      {/* KPI Cards */}
      <div className="kpi-cards-row">
        {/* Approval Rate */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-label">First-Pass Approval Rate</span>
            <div className="kpi-card-icon green">
              <Target size={18} />
            </div>
          </div>
          <div className={`kpi-card-value ${getApprovalRateColor(kpis?.approvalRate || 0)}`}>
            {kpis?.approvalRate?.toFixed(1) || '0.0'}%
          </div>
          <div className="kpi-card-target">
            Target: <span>&gt; 90%</span>
          </div>
        </div>

        {/* Total Completed */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-label">Projects Completed</span>
            <div className="kpi-card-icon blue">
              <FileCheck size={18} />
            </div>
          </div>
          <div className="kpi-card-value">
            {kpis?.totalCompleted || 0}
          </div>
          <div className="kpi-card-target">
            During {periodLabel}
          </div>
        </div>

        {/* First Pass */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-label">First-Pass Approvals</span>
            <div className="kpi-card-icon green">
              <CheckCircle size={18} />
            </div>
          </div>
          <div className="kpi-card-value success">
            {kpis?.firstPassCount || 0}
          </div>
          <div className="kpi-card-target">
            No corrections needed
          </div>
        </div>

        {/* Corrections */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-label">Corrections</span>
            <div className="kpi-card-icon orange">
              <XCircle size={18} />
            </div>
          </div>
          <div className={`kpi-card-value ${kpis?.correctionCount > 0 ? 'warning' : ''}`}>
            {kpis?.correctionCount || 0}
          </div>
          <div className="kpi-card-target">
            Required rework
          </div>
        </div>
      </div>

      {/* Approval Rate Progress */}
      <div className="kpi-section">
        <div className="kpi-section-header">
          <h2 className="kpi-section-title">Approval Rate Progress</h2>
        </div>
        <div className="kpi-section-content">
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                {kpis?.firstPassCount || 0} of {kpis?.totalCompleted || 0} projects approved on first pass
              </span>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--gray-200)' }}>
                {kpis?.approvalRate?.toFixed(1) || 0}%
              </span>
            </div>
            <div className="kpi-progress">
              <div
                className={`kpi-progress-bar ${getApprovalRateColor(kpis?.approvalRate || 0)}`}
                style={{ width: `${Math.min(kpis?.approvalRate || 0, 100)}%` }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-6)', fontSize: 'var(--text-xs)', color: 'var(--gray-500)' }}>
            <span>0%</span>
            <span style={{ marginLeft: 'auto' }}>90% (Target)</span>
            <span>100%</span>
          </div>
        </div>
      </div>

      {/* Projects List */}
      {kpis?.projects?.length > 0 && (
        <div className="kpi-section">
          <div className="kpi-section-header">
            <h2 className="kpi-section-title">Completed Projects</h2>
          </div>
          <div className="kpi-section-content">
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>Job #</th>
                  <th>Project Name</th>
                  <th>Completed</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {kpis.projects.map((project) => (
                  <tr key={project.projectId}>
                    <td>{project.jobNumber}-{project.rev || 0}</td>
                    <td>{project.name}</td>
                    <td>{new Date(project.completedAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`kpi-badge ${project.hadCorrections ? 'warning' : 'success'}`}>
                        {project.hadCorrections ? 'Had Corrections' : 'First Pass'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!kpis?.projects || kpis.projects.length === 0) && (
        <div className="kpi-section">
          <div className="kpi-empty">
            <FileCheck className="kpi-empty-icon" />
            <p className="kpi-empty-text">
              No projects completed during this period.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelBuilderDashboard;
